import type { ADDRESS_FLAGS, IOrderAddress } from '@rogue/universal'
import type { IRogue } from '@/js/services/RogueTypes'

// TODO: Move Loqate batch key to SSRServerContext
// Note: This is a seperate key from the look ahead key
const LOQATE_API_KEY = 'ED54-BJ26-JX65-JU67'
const VERIFY_URL = 'https://api.addressy.com/Cleansing/International/Batch/v1.00/json4.ws'

type LoqateAddress = {
  Address1: string,
  Address2: string,
  Country: string,
  AdministrativeArea: string,
  PostalCode: string,
  Locality: string
  DeliveryAddress?: string,
  DeliveryAddress1?: string,
  DeliveryAddress2?: string,
  SubBuilding?: string,
  Thoroughfare?: string,
  Premise?: string,

}

type MatchStatus = {
  AQI: string
  AVC: string
  AddressStatus: string,
  Address1Status: string,
  Address2Status: string,
  AdministrativeAreaStatus: string,
  LocalityStatus: string,
  PostalCodeStatus: string,
  PostalCodePrimaryStatus: string,
  PostalCodeSecondaryStatus: string,
  PostBoxStatus: string,
  DeliveryAddressStatus: string,
  DeliveryAddress1Status: string,
  DeliveryAddress2Status: string,
  ThoroughfareStatus: string,
  PremiseNumberStatus:string
}

export type AVSResponse = {
  original: IOrderAddress,
  suggestion: IOrderAddress,
  originalAddressValid: boolean,
  passedQualityCheck: boolean,
  isBypassed?: boolean,
  showModal?: boolean,
  suggestedAddressExists?: boolean,
  originalAqi?: string,
}

interface LoqateError {
  Number?: string | number;
  Description?: string;
  Cause?: string;
  Resolution?: string;
}

type LoqateSuccess = {
  Matches: (LoqateAddress & MatchStatus)[],
}[]

type VerifyFlag = 
  | ADDRESS_FLAGS.DPV_CONFIRMED 
  | ADDRESS_FLAGS.PO_BOX 
  | ADDRESS_FLAGS.USER_ENTERED;

// https://support.loqate.com/documentation/reportcodes/fieldstatus/
 
enum LoquateFieldStatus {
  VerifiedNoChange = 1,
  VerifiedAliasChange = 2,
  VerifiedSmallChange = 3,
  VerifiedLargeChange = 4,
  Added = 5,
}

/**
 *
 * @param status
 * @returns Boolean
 * Loops through the returned field status from loqate and checks if any field has a score of > 3. If so we return valid as false
 * Postal code must return a status code of <= 2 to ensure we have a valid extended postal code
 * If AQI is < 'C' the address is unusable
 * https://support.loqate.com/documentation/reportcodes/address-quality-index/
 */

function fieldsValid(status: MatchStatus): boolean {
  return parseInt((status.AddressStatus), 10) <= 3
         && parseInt((status.Address1Status), 10) <= 3
         && parseInt((status.AdministrativeAreaStatus), 10) <= 3
         && parseInt((status.LocalityStatus), 10) <= 3
         && parseInt((status.DeliveryAddressStatus), 10) <= 3
         && parseInt((status.DeliveryAddress1Status), 10) <= 3
         && ['A', 'B', 'C'].includes(status.AQI)
}

function getBypassResponse(inputAddress:IOrderAddress) {

  return {
    original: {
      ...inputAddress,
      verify: {
        date  : Date.now(),
        id    : '',
        flags : ["u"] as VerifyFlag[],
      },
    },
    suggestion: {
      ...inputAddress,
      verify: {
        date  : Date.now(),
        id    : '',
        flags : ["u"] as VerifyFlag[],
      },
    },
    showModal            : false,
    originalAddressValid : false,
    passedQualityCheck   : false,
    isBypassed           : true,
  }  
}

export async function verifyAddress(rogue: IRogue, inputAddress: IOrderAddress): Promise<AVSResponse> {
  if (inputAddress.verify?.flags.length || rogue.site.code !== 'USA') {
    return {
      original             : inputAddress,
      suggestion           : inputAddress,
      originalAddressValid : true,
      passedQualityCheck   : true,
    }
  }
  // Create request body to send to Loqate
  const userAddress: LoqateAddress = {
    Address1           : inputAddress.street[0],
    Address2           : inputAddress.street[1],
    Locality           : inputAddress.city,
    Country            : inputAddress.countryId,
    AdministrativeArea : inputAddress.region,
    PostalCode         : inputAddress.postcode,
  }

  const req = {
    Key       : LOQATE_API_KEY,
    Addresses : [userAddress],
    Options   : {
      Process       : 'Verify',
      ServerOptions : {
        FieldStatus: true,
      },
    },
  }

  try {
    const resp = await rogue.fetch<LoqateSuccess | LoqateError>(VERIFY_URL, 'POST', req)

    if (resp && !Array.isArray(resp) && 'Description' in resp) {
      rogue.log.error('Address Verification Error:', resp.Description || 'Unknown Loqate Error')
    
      return getBypassResponse(inputAddress)
    }

    const match = (resp as LoqateSuccess)[0]?.Matches[0]

    let postalNeedsVerified = false

    if (parseInt((match.PostalCodeSecondaryStatus), 10) === LoquateFieldStatus.Added) {
    // If the postalCode Secondary was 5 then it wasn't entered
    // So long as the primary matched below a value of 2 then append it to the original address, as all orders must have a zip + 4 digit code
      if (parseInt((match.PostalCodePrimaryStatus), 10) <= LoquateFieldStatus.VerifiedAliasChange) {
        inputAddress.postcode = match.PostalCode
      } else {
        postalNeedsVerified = true
      }
    }

    const originalValid = fieldsValid(match) && !postalNeedsVerified
    let street: string[] = []
    if (match.Premise && match.Thoroughfare) {
      const matchedAddress =  `${match.Premise} ${match.Thoroughfare}`.trim()
      street = [matchedAddress, match.SubBuilding || '']
    } else {
      street = [match.DeliveryAddress1 || '', match.SubBuilding || '']
    }

    // Case insensitive compare
    const originalModified = street.join(' ').trim().toUpperCase() !== inputAddress.street.join(' ').toUpperCase().trim()

    const { company, email, firstname, lastname, telephone, vatNumber } = inputAddress

    const deliveryAddressStatus = Number(match.DeliveryAddress1Status)

    const poBoxApproved = (parseInt((match.PostBoxStatus), 10) <= 7) 

    const flags: VerifyFlag[] = [
      originalValid ? 'dpv' : 'u',
      poBoxApproved ? 'po' : null,
    ].filter(Boolean) as VerifyFlag[]

    return {
      originalAqi : match.AQI,
      original    : {
        ...inputAddress,
        verify: {
          date  : Date.now(),
          id    : '',
          // @ts-ignore
          flags : flags,
        },
      },

      originalAddressValid   : originalValid,
      showModal              : originalModified,
      // If the Delivery Address isn't present or isn't valid then we shouldn't suggest a match for the user
      // https://support.loqate.com/documentation/reportcodes/fieldstatus/
      suggestedAddressExists : deliveryAddressStatus <= 5 && match.AQI !== 'E',
      suggestion             : {
        firstname,
        lastname,
        email,
        telephone,
        vatNumber,
        company,
        street,
        city      : match.Locality,
        region    : match.AdministrativeArea,
        postcode  : match.PostalCode,
        countryId : match.Country,
        verify    : {
          date  : Date.now(),
          id    : match.AVC || '',
          // @ts-ignore
          flags : poBoxApproved ? ['dpv', 'po'] : ['dpv'],
        },
      },
    }
  }
  catch(err:any) {
    rogue.log.error('Loqate ', err)
    
    return getBypassResponse(inputAddress)
  }
}
